"use client";

import {
  PinInput as ChakraPinInput,
  type PinInputRootProps,
  type HTMLChakraProps
} from "@chakra-ui/react";

import { asSlotComponent } from "../internal/v3-slot";

const PinInputControl = asSlotComponent(ChakraPinInput.Control);
const PinInputInput = asSlotComponent(ChakraPinInput.Input);
const PinInputHiddenInput = asSlotComponent(ChakraPinInput.HiddenInput);

export type PinInputFieldProps = HTMLChakraProps<"input"> & {
  [dataAttribute: `data-${string}`]: string | undefined;
};

export interface PinInputProps extends Omit<
  PinInputRootProps,
  "value" | "defaultValue" | "onValueChange" | "onValueComplete"
> {
  /** Number of inputs to render. */
  count?: number;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  /** Per-input props, e.g. a `data-testid` derived from the slot position. */
  getInputProps?: (index: number) => PinInputFieldProps;
}

/** v2 exposed the pin as one string; v3 models it as one entry per input. */
const toValueArray = (value?: string) =>
  value == null ? undefined : value.split("");

export function PinInput({
  count = 4,
  value,
  defaultValue,
  onChange,
  onComplete,
  getInputProps,
  name = "pin-input",
  ...props
}: PinInputProps) {
  return (
    <ChakraPinInput.Root
      {...props}
      name={name}
      count={count}
      value={toValueArray(value)}
      defaultValue={toValueArray(defaultValue)}
      onValueChange={(details) => onChange?.(details.valueAsString)}
      onValueComplete={(details) => onComplete?.(details.valueAsString)}
    >
      <PinInputHiddenInput />
      <PinInputControl>
        {Array.from({ length: count }, (_, index) => (
          <PinInputInput
            key={index}
            index={index}
            {...getInputProps?.(index)}
          />
        ))}
      </PinInputControl>
    </ChakraPinInput.Root>
  );
}
